import { Box, Button, Card, Grid2, Link, Typography, alpha, useTheme } from '@mui/material';
import { ReactComponent as LogoDark } from 'assets/djplayit-dark.svg';
import { ReactComponent as LogoLight } from 'assets/djplayit-light.svg';
import { Login } from 'components/auth/Login';
import { Register } from 'components/auth/Register';
import { ResetPassword } from 'components/auth/ResetPassword';
import { VerifyEmail } from 'components/auth/VerifyEmail';
import { DownloadClientButton } from 'components/common/DownloadButton';
import { Updater } from 'components/common/Updater';
import { WebComponent } from 'components/guardedComponent/WebComponents';
import { UserCredential, sendEmailVerification } from 'firebase/auth';
import { useUser } from 'hooks/useUser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { appUrl } from 'utils/appUrl';

export interface AuthProps {
  section?: 'resetPassword' | 'register' | 'verifyEmail';
}

export function Auth(props: AuthProps) {
  const { t } = useTranslation();
  // const [showLogin, setShowLogin] = useState(true);
  const [userData, setUserData] = useState<{ username: string; token: string | null } | null>(null);
  const [userCredential, setUserCredential] = useState<UserCredential | null>(null);
  const navigate = useNavigate();
  const { setUser, setSpotifyToken, setRedirectUri, setStartSpotify } = useUser();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode == 'dark';

  const mainBackground = isDarkMode
    ? 'linear-gradient(135deg, #1b0052, #001864, #003675, #004f61, #006043, #006638)'
    : 'linear-gradient(135deg, #6140ff, #406dff, #50a1ff, #31d9ff, #1fffbc, #2fffa1)';

  const cardSx = {
    borderRadius: 3,
    p: 3,
    mb: 3,
    background: isDarkMode
      ? alpha(theme.palette.common.black, 0.25)
      : alpha(theme.palette.common.white, 0.5),
  };

  const handleRegisterSuccess = async (
    username: string,
    token: string | null,
    userCredential: UserCredential
  ) => {
    setUser(userCredential.user);
    setSpotifyToken('');
    setUserData({ username, token });
    setUserCredential(userCredential);
    setRedirectUri('');
    setStartSpotify(false);

    await sendEmailVerification(userCredential.user, {
      url: appUrl(`?email=${username}`).toString(),
    });

    navigate('/auth/verifyEmail');
  };

  const handleLoginSuccess = (
    username: string,
    token: string | null,
    userCredential: UserCredential
  ) => {
    setUser(userCredential.user);
    setSpotifyToken('');
    setUserData({ username, token });
    setUserCredential(userCredential);
    setStartSpotify(false);
    navigate('/');
  };

  const handlePasswordResetSuccess = () => {};

  // useEffect(() => {
  //   if (user != null) {
  //     navigate('/');
  //   }
  // }, [user]);

  return (
    <>
      <Grid2 container sx={{ minHeight: '100vh', pt: 5, background: mainBackground, mb: '-20px' }}>
        <Grid2 size={12}>
          <Box
            sx={{
              mx: 'auto',
              py: 5,
              width: 300,
            }}
          >
            {isDarkMode ? <LogoLight /> : <LogoDark />}
          </Box>
          <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
            <Updater />
            {!props.section && (
              <>
                <Card sx={cardSx}>
                  <Login onSuccess={handleLoginSuccess} />
                </Card>
                <WebComponent hideOnClient>
                  <Card sx={cardSx}>
                    <DownloadClientButton fullWidth />
                  </Card>
                </WebComponent>
                {/* <Card sx={cardSx}>
                  <Button
                    component={RouterLink}
                    size="large"
                    variant="outlined"
                    color="primary"
                    to="/auth/register"
                    fullWidth
                  >
                    Create new account
                  </Button>
                </Card> */}
              </>
            )}
            {props.section == 'register' && (
              <>
                <Card sx={cardSx}>
                  <Register onSuccess={handleRegisterSuccess} />
                </Card>
                <Card sx={cardSx}>
                  <Typography textAlign="center">
                    {t('alreadyHaveAnAccount', 'Already have an account?')}{' '}
                    <Link component={RouterLink} to="/auth">
                      {t('logIn', 'Log in')}
                    </Link>
                  </Typography>
                </Card>
              </>
            )}
            {props.section == 'resetPassword' && (
              <>
                <Card sx={cardSx}>
                  <ResetPassword onSuccess={handlePasswordResetSuccess} />
                </Card>
                <Card sx={cardSx}>
                  <Button
                    component={RouterLink}
                    size="large"
                    variant="outlined"
                    color="primary"
                    to="/auth"
                    fullWidth
                  >
                    {t('backToTheLogin', 'Back to the login')}
                  </Button>
                </Card>
              </>
            )}
            {props.section == 'verifyEmail' && (
              <>
                <Card sx={cardSx}>
                  <VerifyEmail />
                </Card>
                <Card sx={cardSx}>
                  <Button
                    component={RouterLink}
                    size="large"
                    variant="outlined"
                    color="primary"
                    to="/auth"
                    fullWidth
                  >
                    {t('backToTheLogin', 'Back to the login')}
                  </Button>
                </Card>
              </>
            )}
          </Box>
        </Grid2>
      </Grid2>
    </>
  );
}
